import { RightPanel } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Agenda from "./agenda/agenda";
import AgendaOpen from "./agenda-open/agenda-open";
import MultipleAgenda from "./multipleAgenda/multipleAgenda";
import EventsAgenda from "./eventsAgenda/eventsAgenda";
import styles from "./agendas.module.scss";
import {
  LegendData,
  AgendasSize,
  EventLegendData,
  EventData
} from "helpers/models";
import { ReactComponent as LegendCloseSvg } from "assets/legend_close.svg";
import { ReactComponent as LegendOpenSvg } from "assets/legend_open.svg";
import { Tooltip } from "@mui/material";
import { clearTcTrack } from "containers/events/tc-track/tc-track";
import { MapContext } from "helpers/map";
import { DrawShapeAgenda } from "./draw-shape-legend/DrawShapeAgenda.module";

interface Props {
  size: string;
}

function Agendas(props: Props) {
  const [context, dispatch] = useContext(AppContext);
  const drawLineLegendsLegends = context.drawLineLegendsLegends || [];
  const [isShowLegend, setIsShowLegend] = useState(false);
  const [legendType, setLegendType] = useState(true);
  const [t] = useTranslation();
  const { map } = useContext(MapContext);

  const deleteShapeWithLegend = (shapeId: string) => {
    const shapeEntry = context.shapes.find((pl) => pl.id === shapeId);

    const currentShape = context.currentShape as any;
    const isDeletingCurrentShape =
      !!currentShape &&
      (currentShape.id === shapeId ||
        currentShape.shapeId === shapeId ||
        (!!shapeEntry?.shapeId && currentShape.id === shapeEntry.shapeId) ||
        (!!shapeEntry?.shapeId && currentShape.shapeId === shapeEntry.shapeId));

    if (
      isDeletingCurrentShape &&
      (context.rightPanel === RightPanel.Line ||
        context.rightPanel === RightPanel.Polygon)
    ) {
      dispatch({ type: AppAction.setCurrentShape, payload: undefined });
      dispatch({ type: AppAction.setLineAnalysisPanelVisible, payload: false });
      dispatch({
        type: AppAction.setRightPanel,
        payload: context.currentMultipleLocations
          ? RightPanel.Multiple
          : RightPanel.Empty
      });
    }

    if (shapeEntry?.aMapRef && map) {
      map.remove(shapeEntry.aMapRef);
    }

    dispatch({
      type: AppAction.deleteShape,
      payload: shapeId
    });
  };

  const deleteLayer = (item: LegendData) => {
    item.layerInfo.layerObjEn.hide();
    item.layerInfo.layerObjCn && item.layerInfo.layerObjCn.hide();
    item.layerInfo.layerObjWms && item.layerInfo.layerObjWms.hide();
    item.layerInfo.checked = false;

    let layers = context.layerList?.map((item: any) => {
      item.children = item.children.map((i: any) => {
        if (i.id === item.id) {
          i.checked = false;
        }
        return i;
      });
      return item;
    });

    dispatch({ type: AppAction.setLayerList, payload: layers });
    dispatch({ type: AppAction.updateLayerLegend, payload: item.layerInfo });
  };

  const deleteEventsLayer = (item: EventLegendData) => {
    clearTcTrack(item.layerInfo.id, map);
    item.layerInfo.layerObj.hide(0);
    item.layerInfo.checked = false;
    let data = context.eventsList!.map((event: EventData) => {
      if (event.id === item.layerInfo.id) {
        event.checked = false;
        event.layerObj.hide(0);
        return event;
      }
      return event;
    });
    let selectedEventsList = context.selectedEventsList.filter(
      (event: EventData) => event.id !== item.layerInfo.id
    );
    dispatch({
      type: AppAction.setSelectedEventsList,
      payload: selectedEventsList
    });
    selectedEventsList.length === 0 &&
      context.rightPanel === RightPanel.Events &&
      dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Empty });
    selectedEventsList.length === 0 &&
      dispatch({
        type: AppAction.setCurrentEventsLocationInfo,
        payload: undefined
      });
    dispatch({ type: AppAction.setEventsList, payload: data });
    dispatch({ type: AppAction.updateEventsLegend, payload: item.layerInfo });
  };

  const agendasClass = () => {
    switch (props.size) {
      case AgendasSize.S:
        return styles.agendasS;
      case AgendasSize.M:
        return styles.agendasM;
      case AgendasSize.L:
        return styles.agendasL;
      default:
        return styles.agendasL;
    }
  };

  useEffect(() => {
    if (context.currentMultipleLocations) {
      setIsShowLegend(true);
    } else {
      if (
        context.layerLegend ||
        context.eventsLegend ||
        drawLineLegendsLegends.length > 0
      ) {
        const num = context.layerLegend
          ? context.layerLegend.filter((item) => item.layerInfo.checked).length
          : 0;
        const num1 = context.eventsLegend
          ? context.eventsLegend.filter((item) => item.layerInfo.checked).length
          : 0;
        setIsShowLegend((num || num1 || drawLineLegendsLegends.length) > 0);
      } else {
        setIsShowLegend(false);
      }
    }
  }, [
    context.currentMultipleLocations,
    context.layerLegend,
    context.eventsLegend,
    drawLineLegendsLegends
  ]);

  return (
    <>
      {isShowLegend && (
        <div
          className={agendasClass()}
          style={{
            right:
              context.rightPanel === RightPanel.Empty || context.arrowRight
                ? "200px"
                : "600px"
          }}
        >
          <div className={styles.legendType}>
            <Tooltip
              className="p-[6px]"
              title={
                legendType ? t("layers.legendClose") : t("layers.legendOpen")
              }
              placement={legendType ? "right" : "top"}
              arrow
            >
              {legendType ? (
                <LegendCloseSvg
                  onClick={() => setLegendType(false)}
                  className="fill-[#0009]"
                />
              ) : (
                <LegendOpenSvg
                  onClick={() => setLegendType(true)}
                  className="fill-[#0009]"
                />
              )}
            </Tooltip>
          </div>

          <div
            style={{ display: legendType ? "block" : "none" }}
            className={styles.legendClose}
          >
            {context.currentMultipleLocations && <MultipleAgenda />}
            {context.eventsLegend &&
              context.eventsLegend.map(
                (item, index) =>
                  item.layerInfo.checked && (
                    <EventsAgenda
                      {...item}
                      onClose={() => deleteEventsLayer(item)}
                      key={item.layerId}
                    />
                  )
              )}
            {context.layerLegend &&
              context.layerLegend.map(
                (item, index) =>
                  item.layerInfo.checked && (
                    <Agenda
                      {...item}
                      onClose={() => deleteLayer(item)}
                      key={item.layerId}
                    />
                  )
              )}
            {/* 手绘线图例区域 */}
            {drawLineLegendsLegends && drawLineLegendsLegends.length > 0 && (
              <div className={styles.drawLineLegendSection}>
                {drawLineLegendsLegends.map((legend) => (
                  <DrawShapeAgenda
                    key={legend.shapeId}
                    legend={legend}
                    onClose={deleteShapeWithLegend}
                  />
                ))}
              </div>
            )}
          </div>

          {!legendType && (
            <div className={styles.legendOpen}>
              {(context.currentMultipleLocations ||
                context.eventsLegend?.some((item) => item.layerInfo.checked) ||
                context.layerLegend?.some((item) => item.layerInfo.checked) ||
                drawLineLegendsLegends.length > 0) && (
                <AgendaOpen
                  onClose={(event) => deleteLayer(event)}
                  onEventsClose={(event) => deleteEventsLayer(event)}
                  drawShapeLegends={drawLineLegendsLegends}
                  onDrawShapeClose={deleteShapeWithLegend}
                />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Agendas;
